import { apiGet, apiPatch, apiPost } from '@/shared/utils/requestBuilder';

import type {
  AnexarComprovanteFormData,
  CancelarFaturaFormData,
  EnviarSegundaViaFormData,
  Fatura,
  GerarFaturaDeApuracaoFormData,
  GerarFaturaManualFormData,
  GerarFaturasLoteFormData,
  ReemitirFaturaFormData,
} from '../types';
import { projectApiInvoice, type ApiInvoice, type ApiListInvoicesResponse } from './apiTypes';

async function readJson<T>(response: Response, fallbackMessage: string): Promise<T> {
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(json?.error?.message ?? fallbackMessage);
  }
  return json.data as T;
}

export interface EligibleTenantDto {
  id: string;
  name: string;
  inTrial: boolean;
  cnpj: string | null;
  clientType: 'CONTRACTED' | 'ONE_TIME';
}

export const faturasService = {
  async fetchEligibleTenants(): Promise<EligibleTenantDto[]> {
    const response = await apiGet('/billing/eligible-tenants');
    return readJson<EligibleTenantDto[]>(response, 'Falha ao listar clientes elegíveis');
  },
  async fetchAll(): Promise<Fatura[]> {
    const response = await apiGet('/billing');
    const data = await readJson<ApiListInvoicesResponse>(response, 'Falha ao listar faturas');
    return data.items.map(projectApiInvoice);
  },

  async fetchById(faturaId: string): Promise<Fatura> {
    const response = await apiGet(`/billing/${faturaId}`);
    const data = await readJson<ApiInvoice>(response, 'Falha ao carregar fatura');
    return projectApiInvoice(data);
  },

  async gerarFaturaManual(data: GerarFaturaManualFormData): Promise<Fatura> {
    if (data.valor <= 0) throw new Error('Valor inválido');
    if (data.descricao.trim().length < 10) {
      throw new Error('Descrição precisa ter pelo menos 10 caracteres');
    }
    const response = await apiPost('/billing/manual', {
      workspaceId: data.tenantId,
      value: data.valor,
      description: data.descricao.trim(),
      dueAt: data.dataVencimento,
      paymentMethod: data.metodoPagamento,
      customerDocument: data.customerDocument,
    });
    const apiInvoice = await readJson<ApiInvoice>(response, 'Falha ao gerar fatura manual');
    return projectApiInvoice(apiInvoice);
  },

  async gerarFaturaDeApuracao(data: GerarFaturaDeApuracaoFormData): Promise<Fatura> {
    if (data.valorTotal <= 0) {
      throw new Error('Apuração sem valor cobrável — fatura não pode ser gerada');
    }
    const response = await apiPost('/billing/from-cycle', {
      balancePeriodId: data.apuracaoId,
    });
    const apiInvoice = await readJson<ApiInvoice>(
      response,
      'Falha ao gerar fatura a partir da apuração',
    );
    return projectApiInvoice(apiInvoice);
  },

  async gerarFaturasLote(data: GerarFaturasLoteFormData): Promise<{
    sucesso: Fatura[];
    falhas: Array<{ apuracaoId: string; motivo: string }>;
  }> {
    if (data.apuracoesIds.length === 0) {
      throw new Error('Selecione ao menos uma apuração');
    }
    const response = await apiPost('/billing/batch', {
      balancePeriodIds: data.apuracoesIds,
    });
    const result = await readJson<{
      success: ApiInvoice[];
      failures: { balancePeriodId: string; reason: string }[];
    }>(response, 'Falha ao gerar lote');
    return {
      sucesso: result.success.map(projectApiInvoice),
      falhas: result.failures.map((f) => ({ apuracaoId: f.balancePeriodId, motivo: f.reason })),
    };
  },

  async cancelarFatura(data: CancelarFaturaFormData): Promise<Fatura> {
    const response = await apiPatch(`/billing/${data.faturaId}/cancel`, {
      motivo: data.motivo.trim(),
      cienteEnvioCancelado: data.cienteEnvioCancelado,
    });
    const apiInvoice = await readJson<ApiInvoice>(response, 'Falha ao cancelar fatura');
    return projectApiInvoice(apiInvoice);
  },

  async reemitirFatura(data: ReemitirFaturaFormData): Promise<{ original: Fatura; nova: Fatura }> {
    const response = await apiPost(`/billing/${data.faturaId}/reissue`, {
      value: data.valor,
      dueAt: data.dataVencimento,
      description: data.descricao,
      motivo: data.motivo.trim(),
    });
    const nova = await readJson<ApiInvoice>(response, 'Falha ao reemitir fatura');
    const original = await this.fetchById(data.faturaId);
    return { original, nova: projectApiInvoice(nova) };
  },

  async enviarSegundaVia(data: EnviarSegundaViaFormData): Promise<Fatura> {
    const response = await apiPost(`/billing/${data.faturaId}/send-second-copy`, {
      channels: data.canais,
      customMessage: data.mensagemPersonalizada ?? undefined,
    });
    const apiInvoice = await readJson<ApiInvoice>(response, 'Falha ao registrar segunda via');
    return projectApiInvoice(apiInvoice);
  },

  async anexarComprovante(data: AnexarComprovanteFormData & { file: File }): Promise<Fatura> {
    const form = new FormData();
    form.append('file', data.file, data.arquivoNome);
    form.append('paidAt', data.dataPagamento.slice(0, 10));
    form.append('confirmedValue', String(data.valorConfirmado));
    if (data.observacao) form.append('note', data.observacao);
    const response = await apiPost(`/billing/${data.faturaId}/receipt`, form);
    const apiInvoice = await readJson<ApiInvoice>(response, 'Falha ao anexar comprovante');
    return projectApiInvoice(apiInvoice);
  },

  async tentarReemissaoAsaas(faturaId: string): Promise<Fatura> {
    const response = await apiPost(`/billing/${faturaId}/retry-asaas`, {});
    const apiInvoice = await readJson<ApiInvoice>(response, 'Falha ao tentar reemissão no Asaas');
    return projectApiInvoice(apiInvoice);
  },
};
